/**
 * useBattlePuzzles
 *
 * GET /learning/puzzles/by_ids/?ids=1,2,3
 *
 * Hydrate the puzzle rows for an active Position Trainer Battle. The
 * adapter receives `coords: string[]` of puzzle ids at session start and
 * passes them here to fetch fen / moves / themes / rating in a single
 * roundtrip. Puzzles are immutable so the cache stale-time is infinite.
 */

import {useQuery} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';
import type {BattlePuzzle} from '@/Screens/FunChallenges/types';

interface BattlePuzzlesResponse {
  puzzles: Record<string, BattlePuzzle>;
}

export default function useBattlePuzzles(ids: string[]) {
  const sortedKey = (ids || []).slice().sort().join(',');
  return useQuery<BattlePuzzlesResponse, unknown>(
    ['fun_ptb_puzzles', sortedKey],
    async () => {
      if (!ids || ids.length === 0) return {puzzles: {}};
      const response = await ChessRestClient.get(
        'learning/puzzles/by_ids/?ids=' + encodeURIComponent(ids.join(',')),
      );
      return response.data as BattlePuzzlesResponse;
    },
    {
      enabled: !!ids && ids.length > 0,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
}
